import React from 'react';
import { Mic2, Heart, Sparkles } from 'lucide-react';
import coupleImg from '../assets/couple.jpg';
import beachCoupleImg from '../assets/beach_couple.jpg';

const Login = () => {
  const handleLogin = () => {
    const redirectUrl = `${window.location.origin}/`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-purple-50 flex items-center justify-center p-4 relative overflow-hidden">
      <div
        className="pointer-events-none select-none fixed top-0 left-0 z-0"
        data-testid="landing-beach-couple-image"
        style={{ transform: 'rotate(8deg) translate(-10%, -14%)' }}
      >
        <img
          src={beachCoupleImg}
          alt="Happy couple on the beach"
          className="w-[380px] max-w-[50vw] rounded-3xl shadow-2xl opacity-90 border-8 border-white"
        />
      </div>
      <div
        className="pointer-events-none select-none fixed bottom-0 right-0 z-0"
        data-testid="landing-couple-image"
        style={{ transform: 'rotate(-10deg) translate(8%, 12%)' }}
      >
        <img
          src={coupleImg}
          alt="Happy couple sharing headphones"
          className="w-[420px] max-w-[60vw] rounded-3xl shadow-2xl opacity-90 border-8 border-white"
        />
      </div>
      <div className="max-w-6xl w-full grid md:grid-cols-2 gap-12 items-center relative z-10">
        {/* Left side - Branding */}
        <div className="text-center md:text-left space-y-6">
          <div className="inline-block">
            <div className="flex items-center space-x-3 bg-white px-6 py-3 rounded-full shadow-lg">
              <Mic2 className="w-14 h-14 text-pink-500" />
              <h1 className="text-4xl font-bold gradient-text">Soundlyke</h1>
            </div>
          </div>
          
          <h2 className="text-5xl sm:text-6xl font-bold text-gray-900 leading-tight">
            Find Love<br/>Through<br/>
            <span className="gradient-text">Your Voice</span>
          </h2>
          
          <p className="text-lg text-gray-600 max-w-md">
            Connect with people based on what truly matters - personality, humor, and authenticity. 
            Hear their story before you see their face.
          </p>
          
          <div className="flex flex-wrap gap-4 pt-4">
            <div className="flex items-center space-x-2">
              <Heart className="w-5 h-5 text-pink-500" />
              <span className="text-sm text-gray-600">Voice-First Matching</span>
            </div>
            <div className="flex items-center space-x-2">
              <Sparkles className="w-5 h-5 text-purple-500" />
              <span className="text-sm text-gray-600">Authentic Connections</span>
            </div>
          </div>
        </div>

        {/* Right side - Login Card */}
        <div className="glass rounded-3xl p-8 sm:p-12 shadow-2xl">
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <h3 className="text-2xl font-bold text-gray-900">Welcome to Soundlyke</h3>
              <p className="text-gray-600">Sign in to start your journey</p>
            </div>

            <div className="space-y-4">
              <button
                onClick={handleLogin}
                data-testid="google-login-btn"
                className="w-full btn-primary flex items-center justify-center space-x-3 py-4 text-lg"
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span>Continue with Google</span>
              </button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500">Why Soundlyke?</span>
                </div>
              </div>

              <div className="space-y-3 text-sm text-gray-600">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-pink-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Mic2 className="w-4 h-4 text-pink-500" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Voice Bio</p>
                    <p>Share your personality through your voice</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Heart className="w-4 h-4 text-purple-500" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Authentic Matching</p>
                    <p>Connect based on personality, not just looks</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-pink-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Sparkles className="w-4 h-4 text-pink-500" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Unlock Photos</p>
                    <p>Upgrade to see profile photos after matching</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center text-xs text-gray-500 pt-4">
              By continuing, you agree to our Terms of Service and Privacy Policy
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;